import type { IWhitelabelControlOption } from '@/Interfaces/ControlOption.interface';
import { IFilterFormData } from '@/Interfaces/PdfBuilder/FilterFormData.interface';
import { IScheduledPdfCustomReport } from '@/Interfaces/PdfBuilder/ScheduledPdfCustomReport.interface';
import { ISchedulePdfReportInitialValues } from '@/Interfaces/PdfBuilder/ScheduledPdfReportInitialValues.interface';
import type { IPdfCustomReportTemplate } from '@/Interfaces/PdfBuilder/Template.interface';
import { ICustomReportListRequestPayload } from '@/Interfaces/ReportBuilder/RequestPayload.interface';
import ShareModal from '@/components/Modal/ShareModal';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import { getDefaultFilters, hydratePdfTemplate } from '@/helpers/pdfBuilder';
import ScheduleReportModal from '@/pages/PdfBuilder/Components/ScheduleReportModal';
import Filter from '@/pages/PdfBuilder/Filter';
import { usePdfReportActions } from '@/pages/PdfBuilder/Hooks/usePdfReportActions';
import { usePdfScheduleActions } from '@/pages/PdfBuilder/Hooks/usePdfScheduleActions';
import { usePdfTemplateReports } from '@/pages/PdfBuilder/Hooks/usePdfTemplateReports';
import ScheduledReportsGrid from '@/pages/PdfBuilder/ScheduledReportsGrid';
import PdfTemplateReportsGrid from '@/pages/PdfBuilder/TemplateReportsGrid';
import ReportNoticeModal from '@/pages/ReportBuilder/Components/ReportNoticeModal';
import SaveAsTemplateModal from '@/pages/ReportBuilder/Components/SaveAsTemplateModal';
import GeneratedReportsGrid from '@/pages/ReportBuilder/GeneratedReportsGrid';
import { useModal } from '@/pages/ReportBuilder/Hooks/useModal';
import { createPdfTemplate, fetchPdfTemplate } from '@/store/api/pdfBuilderTemplate.api';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectHideWhitelabels, selectMeRole } from '@/store/selectors/me.selectors';
import {
  selectPdfBuilderGeneratedReports,
  selectPdfBuilderScheduledReports,
} from '@/store/selectors/pdfBuilder.selectors';
import { selectParsedAllClients, selectParsedAllWhitelabels } from '@/store/selectors/sharedApi.selectors';
import { getGeneratedReports, getScheduledReports } from '@/store/slices/pdfBuilder.slice';
import { getClients, getWhitelabels } from '@/store/slices/sharedApi.slice';
import { ScheduledReportFilterDate } from '@/types/ReportBuilder/FilterDate.types';
import { SharesTargets } from '@/types/shares.types';
import { Paper } from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

const PdfBuilder = () => {
  const dispatch = useAppDispatch();

  const hideWhitelabels = useAppSelector(selectHideWhitelabels);
  const meRole = useAppSelector(selectMeRole);
  const { generatedReports, loadingGeneratedReports, generatedReportsTotal } = useAppSelector(
    selectPdfBuilderGeneratedReports,
  );
  const { scheduledReports, loadingScheduledReports, scheduledReportsTotal } = useAppSelector(
    selectPdfBuilderScheduledReports,
  );

  const allClients = useAppSelector(selectParsedAllClients);
  const allWhitelabels = useAppSelector(selectParsedAllWhitelabels);

  const filterFormMethods = useForm<IFilterFormData>({
    defaultValues: getDefaultFilters(),
    mode: 'onChange',
  });
  const { control, getValues, setValue } = filterFormMethods;
  const dateRangeFormat = useWatch({ control, name: 'dateRangeFormat' }) as ScheduledReportFilterDate;

  const [editingScheduledReport, setEditingScheduledReport] = useState<IScheduledPdfCustomReport | null>(null);

  const noticeModalState = useModal();
  const scheduleModalState = useModal();
  const templateModalState = useModal();
  const templateReports = usePdfTemplateReports();

  useEffect(() => {
    if (!hideWhitelabels) {
      dispatch(getWhitelabels());
    } else {
      dispatch(getClients());
    }
  }, [dispatch]);

  const loadGeneratedReports = useCallback(
    (payload?: ICustomReportListRequestPayload) => {
      dispatch(getGeneratedReports(payload ?? {}));
    },
    [dispatch],
  );

  const loadScheduledReports = useCallback(
    (payload?: ICustomReportListRequestPayload) => {
      dispatch(getScheduledReports(payload ?? {}));
    },
    [dispatch],
  );

  const { onSubmitFilters, onReportDownload, loadingReportRun } = usePdfReportActions({
    meRole,
    onReportGenerated: noticeModalState.onOpen,
    onFinally: loadGeneratedReports,
  });

  const { onScheduleSubmit, onCancelScheduled } = usePdfScheduleActions({
    editingScheduledReport,
    getValues,
    meRole,
    onAfterSuccess: loadScheduledReports,
  });

  const onScheduleClick = useCallback(() => {
    setEditingScheduledReport(null);
    scheduleModalState.onOpen();
  }, [scheduleModalState.onOpen]);

  const onScheduleModalClose = useCallback(() => {
    scheduleModalState.onClose();
    setEditingScheduledReport(null);
  }, [scheduleModalState.onClose]);

  const onEditScheduled = useCallback(
    (report: IScheduledPdfCustomReport) => {
      setEditingScheduledReport(report);
      scheduleModalState.onOpen();
    },
    [scheduleModalState.onOpen],
  );

  const reloadTemplates = templateReports.reloadTemplates;

  const loadTemplateFilters = useCallback(
    async (id: number | undefined) => {
      if (!id) return;
      try {
        const template = await fetchPdfTemplate(id);
        const hydrated = hydratePdfTemplate(
          template as IPdfCustomReportTemplate,
          allClients,
          allWhitelabels as unknown as IWhitelabelControlOption[],
        );
        setValue('dateRange', hydrated.dateRange);
        setValue('dateRangeFormat', hydrated.dateRangeFormat);
        setValue('platforms', hydrated.platforms);
        setValue('deliveryReports', hydrated.deliveryReports);
        setValue('attributionReports', hydrated.attributionReports);
        setValue('clients', hydrated.clients);
        setValue('whitelabels', hydrated.whitelabels);
        setValue('advertisers', hydrated.advertisers);
      } catch {
        toast.error('Failed to load the template');
      }
    },
    [allClients, allWhitelabels, setValue],
  );

  const onCreateNewTemplate = useCallback(
    async (templateName: string) => {
      await createPdfTemplate(templateName, getValues());
      reloadTemplates();
    },
    [getValues, reloadTemplates],
  );

  const templateOptions = useMemo(
    () => templateReports.templates.map(({ id, name }) => ({ id, name })),
    [templateReports.templates],
  );
  const templateNames = useMemo(() => templateReports.templates.map(({ name }) => name), [templateReports.templates]);

  const editInitialValues = useMemo<ISchedulePdfReportInitialValues | undefined>(
    () =>
      editingScheduledReport
        ? {
            reportName: editingScheduledReport.name,
            interval: editingScheduledReport.interval,
            recipientEmails: editingScheduledReport.recipientEmails,
            startDate: moment(editingScheduledReport.deliveryStartAt),
            dateRangeFormat: (editingScheduledReport.dateRangeFormat ?? dateRangeFormat) as ScheduledReportFilterDate,
          }
        : undefined,
    [editingScheduledReport, dateRangeFormat],
  );

  return (
    <>
      <div className='w-full p-[30px]'>
        <LoadingBackdropWrapper backdropOpen={loadingReportRun}>
          <FormProvider {...filterFormMethods}>
            <Filter
              templates={templateOptions}
              loadingTemplates={templateReports.loading}
              loadTemplateFilters={loadTemplateFilters}
              onSaveAsTemplateClick={templateModalState.onOpen}
              onSubmit={onSubmitFilters}
              onScheduleReportClick={onScheduleClick}
            />
          </FormProvider>
          <Paper className='grid grid-cols-1 mt-8'>
            <GeneratedReportsGrid
              data={generatedReports}
              loading={loadingGeneratedReports}
              loadGeneratedReports={loadGeneratedReports}
              rowCount={generatedReportsTotal}
              onReportDownload={onReportDownload}
            />
          </Paper>
          <Paper className='grid grid-cols-1 mt-8'>
            <ScheduledReportsGrid
              data={scheduledReports}
              loading={loadingScheduledReports}
              loadScheduledReports={loadScheduledReports}
              rowCount={scheduledReportsTotal}
              onEdit={onEditScheduled}
              onCancel={onCancelScheduled}
            />
          </Paper>
          <Paper className='grid grid-cols-1 mt-8'>
            <PdfTemplateReportsGrid
              data={templateReports.templates}
              loading={templateReports.loading}
              onShareTemplate={templateReports.onShareTemplate}
              onDeleteTemplate={templateReports.onDeleteTemplate}
            />
          </Paper>
        </LoadingBackdropWrapper>
      </div>
      <ReportNoticeModal open={noticeModalState.open} onClose={noticeModalState.onClose} />
      <SaveAsTemplateModal
        open={templateModalState.open}
        onClose={templateModalState.onClose}
        onSubmit={onCreateNewTemplate}
        templateNames={templateNames}
      />
      <ShareModal
        open={templateReports.shareTargetId !== null}
        targetTable={SharesTargets.PDF_CUSTOM_REPORT_TEMPLATES}
        targetId={templateReports.shareTargetId ?? 0}
        title='Share Template'
        shareWithOrgHelperText='*This template will be shared with all of your team members'
        onModalClose={templateReports.onShareModalClose}
        onSubmit={templateReports.onSubmitShare}
      />
      <ScheduleReportModal
        open={scheduleModalState.open}
        onClose={onScheduleModalClose}
        onSubmit={onScheduleSubmit}
        dateRangeFormat={dateRangeFormat}
        mode={editingScheduledReport ? 'edit' : 'create'}
        initialValues={editInitialValues}
      />
    </>
  );
};

export default PdfBuilder;
